import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { ridesApi } from './rides-api';
import { rideKeys } from './query-keys';
import type { Ride, RideTracking } from './rides-types';

function isActive(r: Ride): boolean {

  return (
    r.status === 'ACTIVE' &&
    r.rideState !== 'RIDE_CANCELLED' &&
    !(r.rideState === 'RIDE_ENDED' && r.statusReady)
  );
}

function shouldPollSnapshot(r: Ride): boolean {
  return isActive(r);
}

function trackingStorageKey(orderId: string): string {
  return `ride:${orderId}:tracking:v1`;
}

function storedTracking(orderId: string): RideTracking | undefined {
  try {
    const value = sessionStorage.getItem(trackingStorageKey(orderId));
    return value ? (JSON.parse(value) as RideTracking) : undefined;
  } catch {
    return undefined;
  }
}

function storeTracking(orderId: string, tracking: RideTracking): void {
  try {
    sessionStorage.setItem(trackingStorageKey(orderId), JSON.stringify(tracking));
  } catch {
    // The marker remains available for this mount when browser storage is unavailable.
  }
}

function claimProtocolAction(orderId: string, action: 'track' | 'status'): boolean {
  const version = action === 'status' ? 'v3' : 'v2';
  const key = `ride:${orderId}:${action}:sent:${version}`;
  if (localStorage.getItem(key)) return false;
  localStorage.setItem(key, '1');
  return true;
}

/**
 * Live order snapshot for a ride. Polls GET /rides/{id} every 5s (reads the latest
 * stored snapshot, updated by unsolicited callbacks). Sends each protocol-required
 * track/status action once at its lifecycle boundary, never on an interval. Keeps
 * reading snapshots through RIDE_ENDED until the final fare update completes the ride.
 * `fast` bumps the snapshot poll to 1.5s during the cancel handshake.
 */
export function useRideLive(orderId: string, fast = false) {
  const [tracking, setTracking] = useState<RideTracking | undefined>(() => storedTracking(orderId));
  const query = useQuery({
    queryKey: rideKeys.detail(orderId),
    queryFn: () => ridesApi.ride(orderId),
    refetchInterval: (q) => {
      if (fast) return 1500;
      const r = q.state.data;
      if (!r) return 5000;
      return shouldPollSnapshot(r) ? 5000 : false;
    },
  });

  const ride = query.data;
  const active = ride ? isActive(ride) : false;

  // Restore an existing driver location after reload without emitting another track.
  useEffect(() => {
    if (tracking) return;
    let cancelled = false;
    void ridesApi
      .tracking(orderId)
      .then((nextTracking) => {
        if (cancelled) return;
        setTracking(nextTracking);
        storeTracking(orderId, nextTracking);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [orderId, tracking]);

  // Workbench expects one explicit track only after its unsolicited on_track and
  // on_status callbacks. The server derives this gate from their stored timestamps.
  useEffect(() => {
    if (!active || !ride?.trackReady) return;
    if (!claimProtocolAction(orderId, 'track')) return;
    void ridesApi
      .track(orderId)
      .then((nextTracking) => {
        setTracking(nextTracking);
        storeTracking(orderId, nextTracking);
      })
      .catch(() => undefined);
  }, [active, orderId, ride?.trackReady]);

  // Solicited status is the final BAP step. Wait until all unsolicited ride-state and
  // fare-update callbacks have produced the terminal snapshot before sending it.
  useEffect(() => {
    if (!ride?.statusReady) return;
    if (!claimProtocolAction(orderId, 'status')) return;
    void ridesApi.status(orderId).catch(() => undefined);
  }, [orderId, ride?.statusReady]);

  return {
    ride,
    tracking,
    active,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
